// src/redux/slices/jobsAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { jobsService } from '../../services/jobsService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchJobsAdmin = createAsyncThunk(
    'jobsAdmin/fetchJobsAdmin',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await jobsService.getJobsAdmin(params)
            return {
                jobs: response.data.jobs,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createJob = createAsyncThunk(
    'jobsAdmin/createJob',
    async (jobData, { rejectWithValue }) => {
        try {
            const response = await jobsService.createJob(jobData)
            return response.data.job
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateJob = createAsyncThunk(
    'jobsAdmin/updateJob',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await jobsService.updateJob(id, data)
            return response.data.job
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteJob = createAsyncThunk(
    'jobsAdmin/deleteJob',
    async (id, { rejectWithValue }) => {
        try {
            await jobsService.deleteJob(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const toggleJobStatus = createAsyncThunk(
    'jobsAdmin/toggleJobStatus',
    async (job, { rejectWithValue }) => {
        try {
            const response = await jobsService.updateJob(job._id, { isOpen: !job.isOpen })
            return response.data.job
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const jobsAdminSlice = createSlice({
    name: 'jobsAdmin',
    initialState: {
        jobs: [],
        currentJob: null,
        loading: false,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        },
        error: null
    },
    reducers: {
        clearCurrentJob: (state) => {
            state.currentJob = null
        },
        clearError: (state) => {
            state.error = null
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Jobs Admin
            .addCase(fetchJobsAdmin.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchJobsAdmin.fulfilled, (state, action) => {
                state.loading = false
                state.jobs = action.payload.jobs
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchJobsAdmin.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })

            // Create Job
            .addCase(createJob.pending, (state) => {
                state.loading = true
            })
            .addCase(createJob.fulfilled, (state, action) => {
                state.loading = false
                state.jobs.unshift(action.payload)
                state.error = null
            })
            .addCase(createJob.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })

            // Update Job
            .addCase(updateJob.pending, (state) => {
                state.loading = true
            })
            .addCase(updateJob.fulfilled, (state, action) => {
                state.loading = false
                const index = state.jobs.findIndex(job => job._id === action.payload._id)
                if (index !== -1) {
                    state.jobs[index] = action.payload
                }
                state.error = null
            })
            .addCase(updateJob.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })

            // Delete Job
            .addCase(deleteJob.fulfilled, (state, action) => {
                state.jobs = state.jobs.filter(job => job._id !== action.payload)
                state.error = null
            })

            // Toggle Job Status
            .addCase(toggleJobStatus.fulfilled, (state, action) => {
                const index = state.jobs.findIndex(job => job._id === action.payload._id)
                if (index !== -1) {
                    state.jobs[index] = action.payload
                }
                state.error = null
            })
    }
})

export const { clearCurrentJob, clearError, setPagination } = jobsAdminSlice.actions
export default jobsAdminSlice.reducer